import React, { FC } from "react";
import { Controller } from "react-hook-form";
import FormSelect, { GetOptionLabel, GetOptionValue, SingleValue, StylesConfig } from "react-select";

import { FormError } from "../FormError";
import { FormLabel } from "../FormLabel";

interface IProps {
  wrapperClassName?: string;
  className?: string;
  id: string;
  name: string;
  placeholder?: string;
  title: React.ReactNode;
  titleClasses?: string;
  subTitle?: React.ReactNode;
  subTitleClasses?: string;
  control: any;
  options: { label: string; value: string }[];
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  extraAction?: () => void;
  pairValue?: boolean;
  isLoading?: boolean;
  getOptionLabel?: GetOptionLabel<any> | undefined;
  getOptionValue?: GetOptionValue<any> | undefined;
  onChange?: (val: any) => void;
  styles?: StylesConfig<any, any>;
  theme?: "light" | "dark";
}
type OptionType = { label: string; value: string };

export const _styles = (theme: "light" | "dark") => ({
  control: (styles: any) => {
    return {
      ...styles,
      border: theme === "light" ? "1px solid #f2f2f2" : "1px solid #fff",
      background: theme === "light" ? "#f2f2f2" : "#fff",
      minHeight: 38,
      fontSize: 14,
      borderRadius: 10,
      transition: "background-color 0.2s ease-in-out, border-color 0.2s ease-in-out",

      "&:focus-within": {
        background: "#fff",
        border: "1px solid #4c9aff",
        boxShadow: "none"
      },
      "&:hover": {
        background: "#e9ebef",
        border: "1px solid #dee0e6"
      }
    };
  },
  placeholder: (defaultStyles: any) => {
    return {
      ...defaultStyles,
      color: "#7a869a",
      fontSize: 14
    };
  },
  menu: (base: any) => ({ ...base, overflow: "hidden" }),

  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (styles: any, { isDisabled, isSelected }: any) => {
    return {
      ...styles,
      backgroundColor: isSelected ? "#deebff" : "#fff",
      color: isSelected ? "#0052cc" : "#333",
      borderLeftWidth: 2,
      borderColor: isSelected ? "#0052cc" : "#fff",
      cursor: isDisabled ? "not-allowed" : "default",
      "&:hover": {
        borderLeftWidth: 2,
        borderColor: "#0052cc",
        background: isSelected ? "#deebff" : "#f7f7f7"
      }
    };
  },
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#666"
  })
});

// const _styles: StylesConfig<any, any> = {
//   menu: base => ({ ...base, zIndex: 999 }),
//   control: base => ({
//     ...base,
//     border: "1px solid #e5e7eb !important",
//     borderRadius: "0px !important",
//     boxShadow: "0 !important",
//     textTransform: "capitalize",
//     background: "#fcfdfd",
//     ":hover": {
//       background: "#fafafa"
//     },
//     "&:focus-within": {
//       borderColor: "#2F80ED !important"
//     }
//   }),
//   dropdownIndicator: base => ({
//     ...base,
//     color: "#6b6b6b"
//   }),
//   placeholder: defaultStyles => {
//     return {
//       ...defaultStyles,
//       color: "#9da3af",
//       fontWeight: "300"
//     };
//   }
// };

export const Select: FC<IProps> = ({
  title,
  titleClasses,
  subTitle,
  subTitleClasses,
  className,
  wrapperClassName = "",
  id,
  name,
  options,
  pairValue = false,
  isLoading = false,
  control,
  onChange: _onChange,
  theme = "light",
  styles = _styles(theme),
  ...rest
}) => {
  return (
    <div className={wrapperClassName}>
      {title ? (
        <FormLabel id={name} htmlFor={name} className={titleClasses}>
          {title}
        </FormLabel>
      ) : null}
      {subTitle ? <p className={`text-sm font-light text-gray-600 ${subTitleClasses}`}>{subTitle}</p> : null}
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <>
            <FormSelect
              onChange={(val: SingleValue<OptionType>) => {
                pairValue ? onChange(val) : onChange(val?.value);

                if (typeof _onChange === "function") {
                  pairValue ? _onChange(val) : _onChange(val?.value);
                }
              }}
              onBlur={onBlur}
              value={
                pairValue
                  ? value?.label
                    ? value
                    : null
                  : value
                  ? {
                      label: options.find(val => val.value === value)?.label,
                      value
                    }
                  : null
              }
              id={id}
              options={options}
              className={`max-w-sm rounded-md ${className}`}
              styles={styles}
              isLoading={isLoading}
              {...rest}
            />
            {error ? <FormError error={error?.message} id={id} /> : null}
          </>
        )}
      />
    </div>
  );
};

export function CustomSelect<T>({
  title,
  titleClasses,
  subTitle,
  subTitleClasses,
  className,
  wrapperClassName = "mb-4",
  id,
  name,
  options,
  pairValue = false,
  isLoading = false,
  control,
  theme = "light",
  styles = _styles(theme),
  ...rest
}: Omit<IProps, "options"> & { options: T[] }) {
  return (
    <div className={wrapperClassName}>
      {title ? (
        <FormLabel id={name} htmlFor={name} className={titleClasses}>
          {title}
        </FormLabel>
      ) : null}
      {subTitle ? <p className={`text-sm font-light text-gray-600 ${subTitleClasses}`}>{subTitle}</p> : null}
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <>
            <FormSelect
              onChange={(val: SingleValue<T>) => {
                onChange(val);
              }}
              onBlur={onBlur}
              value={value?.label ? value : null}
              id={id}
              options={options}
              className={`max-w-sm rounded-md ${className}`}
              styles={styles}
              isLoading={isLoading}
              {...rest}
            />
            {error ? <FormError error={error?.message} id={id} /> : null}
          </>
        )}
      />
    </div>
  );
}
